/**
 * Admin (layout del rediseño AI Studio, 2026-10-15). Usuarios y Pendientes arriba (los pendientes con sus acciones
 * visibles); Actividad, Ingesta y Scraper en el grupo de la derecha. La ficha de un usuario se abre en el panel
 * derecho (variante B). Toda acción sobre una cuenta (aprobar, rechazar, cambiar rol, borrar) pide confirmación, y
 * sobre la propia cuenta no se ofrece (el servidor también la rechaza). Solo datos reales de los endpoints que ya
 * existen: /admin/users, /admin/logs (kind = scraper | processing), /admin/cron/status, /activity/* y
 * /admin/demo-requests (solicitudes del botón «Solicitar demo» de la portada). Invitar,
 * suspender, el resumen de costos por mes y la alerta de cada usuario no tienen backend: no se dibujan.
 */
import { KeyboardEvent as ReactKeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { CheckCircle2, Clock, DoorOpen, LogIn, LogOut, MoreHorizontal, PanelRight, Search, Shield, UserPlus } from 'lucide-react';
import Layout from '../components/layout/Layout';
import RightPanes, { PANES_GAP, PANES_WIDTH } from '../components/chat/panes/RightPanes';
import PaneShell from '../components/chat/panes/PaneShell';
import { Columna, FOCO, Relleno } from '../components/v3/Ajustes';
import { Profile, ScraperLog } from '../utils/supabase';
import { adminService, CronStatus, DemoEstado, DemoRequest, LogKind, SchedulerStatus } from '../services/admin.service';
import { activityService, ActivityLog, ActivityStats, RecentActivityLog } from '../services/activity.service';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { tipoCorto } from '../utils/documentoMeta';
import { diaRelativo, hora } from '../utils/fechas';
import { MATERIA_LABELS } from '../utils/constants';

type Pestana = 'usuarios' | 'pendientes' | 'demos' | 'actividad' | 'ingesta' | 'scraper';
type Estado = 'pending' | 'approved' | 'rejected';

const nf = (n: number) => n.toLocaleString('es-CL');
/** «hoy 15:41», «ayer 9:02», «6 oct 10:00». */
export const cuando = (v: string | null | undefined) => (v ? `${diaRelativo(v)} ${hora(v)}` : '—');
const estadoDe = (u: Profile): Estado => u.status ?? 'approved'; // sin la columna (antes de la 116) = aprobado
const nombreDe = (u: Profile) => u.name?.trim() || u.email;
export function iniciales(u: Profile): string {
  const base = u.name?.trim() || u.email.split('@')[0];
  const partes = base.split(/[\s._-]+/).filter(Boolean);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || '?';
}

/** «0 8 * * 1-5» → «Lunes a viernes, 8:00». Lo que no reconoce lo muestra tal cual. */
export function cronLegible(expr: string | undefined): string {
  if (!expr) return '—';
  const m = expr.trim().split(/\s+/);
  if (m.length !== 5 || !/^\d+$/.test(m[0]) || !/^\d+$/.test(m[1]) || m[2] !== '*' || m[3] !== '*') return expr;
  const hh = `${Number(m[1])}:${m[0].padStart(2, '0')}`;
  if (m[4] === '1-5') return `Lunes a viernes, ${hh}`;
  if (m[4] === '*') return `Todos los días, ${hh}`;
  return expr;
}

/** US$ con 2 decimales, o 4 si es menos de un centavo. */
export function usd(n: number): string {
  const dec = n > 0 && n < 0.01 ? 4 : 2;
  return `US$ ${n.toLocaleString('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
}
const costoDe = (l: ScraperLog): number | null => {
  const c = Number(l.metadata?.cost_usd);
  return Number.isFinite(c) ? c : null;
};

const ACTIVIDAD: Record<ActivityLog['activity_type'], string> = { login: 'Inició sesión', logout: 'Cerró sesión', access: 'Entró a la app' };
const ICONO_ACTIVIDAD: Record<ActivityLog['activity_type'], typeof LogIn> = { login: LogIn, logout: LogOut, access: DoorOpen };
const materiaLabel = (m: string) => (MATERIA_LABELS as Record<string, string>)[m.toLowerCase()] || m;

const ETIQUETA_ESTADO: Record<Estado, { texto: string; clase: string }> = {
  pending: { texto: 'Pendiente', clase: 'bg-amber-100/80 text-amber-800' },
  approved: { texto: 'Aprobado', clase: 'bg-emerald-50 text-emerald-700' },
  rejected: { texto: 'Rechazado', clase: 'bg-rose-50 text-rose-700' },
};
const ETIQUETA_DEMO: Record<DemoEstado, { texto: string; clase: string }> = {
  nueva: { texto: 'Nueva', clase: 'bg-indigo-50 text-indigo-700' },
  contactada: { texto: 'Contactada', clase: 'bg-emerald-50 text-emerald-700' },
  descartada: { texto: 'Descartada', clase: 'bg-slate-100 text-slate-600' },
};
const ESTADOS_DEMO: DemoEstado[] = ['nueva', 'contactada', 'descartada'];
const DEMOS_POR_PAGINA = 20;
const ETIQUETA_CORRIDA: Record<string, { texto: string; clase: string }> = {
  completed: { texto: 'Completada', clase: 'bg-emerald-50 text-emerald-700' },
  failed: { texto: 'Falló', clase: 'bg-rose-50 text-rose-700' },
  running: { texto: 'En curso', clase: 'bg-indigo-50 text-indigo-700' },
  stopped: { texto: 'Detenida', clase: 'bg-amber-100/80 text-amber-800' },
};
function Etiqueta({ texto, clase, icono }: { texto: string; clase: string; icono?: ReactNode }) {
  return <span className={`px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap inline-flex items-center gap-1 ${clase}`}>{icono}{texto}</span>;
}

const BOTON_ACCION = `px-3 py-1.5 rounded-lg text-white font-semibold text-[12px] bg-[#4F46E5] hover:bg-indigo-700 active:bg-indigo-800 transition-colors motion-reduce:transition-none disabled:opacity-50 ${FOCO}`;
const BOTON_SECUNDARIO = `px-2 py-1.5 text-[12px] font-medium text-slate-600 hover:text-rose-700 transition-colors motion-reduce:transition-none disabled:opacity-50 rounded ${FOCO}`;
const REINTENTAR = `font-medium text-[#4F46E5] hover:underline rounded ${FOCO}`;

/** Carga perezosa con sus estados: se pide la primera vez que `activo` es verdadero y con `recargar`. */
function useRecurso<T>(cargar: () => Promise<T>, activo: boolean) {
  const [datos, setDatos] = useState<T | null>(null);
  const [error, setError] = useState(false);
  const [pedido, setPedido] = useState(false);
  const recargar = useCallback(async () => {
    setPedido(true);
    setError(false);
    setDatos(null);
    try {
      setDatos(await cargar());
    } catch {
      setError(true);
    }
  }, [cargar]);
  useEffect(() => {
    if (activo && !pedido) recargar();
  }, [activo, pedido, recargar]);
  return { datos, error, cargando: !error && datos === null, recargar };
}

interface Confirmacion {
  titulo: string;
  texto: string;
  boton: string;
  peligro?: boolean;
  accion: () => Promise<void>;
}

/** Ventana de confirmación: foco en «Cancelar», Escape cancela, el foco no sale y vuelve al botón que la abrió. */
function Confirmar({ c, ejecutando, onCancelar, onConfirmar }: { c: Confirmacion; ejecutando: boolean; onCancelar: () => void; onConfirmar: () => void }) {
  const caja = useRef<HTMLDivElement>(null);
  const cancelar = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const previo = document.activeElement as HTMLElement | null;
    cancelar.current?.focus();
    return () => previo?.focus?.();
  }, []);
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !ejecutando) onCancelar(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [ejecutando, onCancelar]);
  const atrapar = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !caja.current) return;
    const foco = Array.from(caja.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
    if (!foco.length) return;
    if (e.shiftKey && document.activeElement === foco[0]) { e.preventDefault(); foco[foco.length - 1].focus(); }
    else if (!e.shiftKey && document.activeElement === foco[foco.length - 1]) { e.preventDefault(); foco[0].focus(); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/30 flex items-center justify-center p-4 z-50" onMouseDown={e => { if (e.target === e.currentTarget && !ejecutando) onCancelar(); }}>
      <div ref={caja} role="dialog" aria-modal="true" aria-labelledby="adm-conf-t" aria-describedby="adm-conf-d" onKeyDown={atrapar} className="bg-white rounded-2xl border border-slate-200 max-w-md w-full p-6 shadow-xl">
        <h3 id="adm-conf-t" className="text-[15px] font-bold text-[#142D4E] mb-2">{c.titulo}</h3>
        <p id="adm-conf-d" className="text-[12.5px] text-slate-600 mb-5 leading-relaxed">{c.texto}</p>
        <div className="flex justify-end gap-2">
          <button ref={cancelar} type="button" onClick={onCancelar} disabled={ejecutando} className={`px-3.5 py-2 rounded-xl border border-slate-200 text-slate-600 text-[12.5px] font-medium hover:bg-slate-50 disabled:opacity-50 ${FOCO}`}>
            Cancelar
          </button>
          <button type="button" onClick={onConfirmar} disabled={ejecutando} className={`px-4 py-2 rounded-xl text-white text-[12.5px] font-semibold disabled:opacity-60 ${FOCO} ${c.peligro ? 'bg-rose-600 hover:bg-rose-700' : 'bg-[#4F46E5] hover:bg-indigo-700'}`}>
            {ejecutando ? 'Guardando…' : c.boton}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Menú ··· de una fila de usuario: Escape o clic fuera lo cierran. */
function MenuUsuario({ etiqueta, children }: { etiqueta: string; children: (cerrar: () => void) => ReactNode }) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  // Al elegir una acción el foco vuelve al botón ···: la confirmación que se abre lo devuelve ahí al cerrarse.
  const cerrar = useCallback(() => { setAbierto(false); boton.current?.focus(); }, []);
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => { if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false); };
    // Escape cierra solo el menú, no el panel (que escucha en window).
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); cerrar(); } };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', tecla);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', tecla); };
  }, [abierto, cerrar]);
  return (
    <div className="w-7 flex justify-end relative" ref={caja} onClick={e => e.stopPropagation()}>
      <button
        ref={boton}
        type="button"
        aria-label={etiqueta}
        aria-expanded={abierto}
        onClick={() => setAbierto(a => !a)}
        className={`p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-opacity motion-reduce:transition-none ${FOCO} ${abierto ? 'opacity-100' : 'md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100'}`}
      >
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {abierto && (
        <div className="absolute right-0 top-7 w-48 bg-white rounded-xl border border-slate-200 shadow-lg py-1 text-[12px] z-20">
          {children(cerrar)}
        </div>
      )}
    </div>
  );
}
const itemMenu = (peligro = false) => `w-full px-3 py-1.5 text-left ${FOCO} ${peligro ? 'text-rose-600 hover:bg-rose-50/50' : 'text-slate-700 hover:bg-slate-50'}`;

/** Fila de corrida (scraper o ingesta). */
function FilaCorrida({ l, ingesta }: { l: ScraperLog; ingesta: boolean }) {
  const e = ETIQUETA_CORRIDA[l.status] || { texto: l.status, clase: 'bg-slate-100 text-slate-700' };
  const costo = costoDe(l);
  const prueba = l.metadata?.dry_run === true;
  const titulo = ingesta
    ? (l.metadata?.job as string) || l.subtipo || `${l.institution_code} · ${tipoCorto(l.tipo)}`
    : [l.institution_code?.toUpperCase(), tipoCorto(l.tipo), l.year || null, (l.subtipo || l.materia) ? materiaLabel((l.subtipo || l.materia) as string) : null].filter(Boolean).join(' · ');
  const detalle = ingesta
    ? [`${nf(l.documents_processed || 0)} documento${l.documents_processed === 1 ? '' : 's'}`, l.metadata?.llm_calls ? `${nf(Number(l.metadata.llm_calls))} llamadas` : null].filter(Boolean).join(' · ')
    : `${nf(l.documents_found || 0)} encontrados · ${nf(l.documents_processed || 0)} procesados · ${nf(l.documents_new || 0)} nuevos`;
  const tramo = l.completed_at ? `${cuando(l.started_at)}–${hora(l.completed_at)}` : cuando(l.started_at);
  return (
    <div className="py-3.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-x-4 gap-y-1.5">
      <div className="min-w-0">
        <div className="font-bold text-[#142D4E] break-words sm:truncate" title={titulo}>{titulo}</div>
        <div className="text-[12px] text-slate-500 tabular-nums">{tramo} · {detalle}</div>
        {l.error_message && <div className="text-[12px] text-rose-700 line-clamp-2" title={l.error_message}>{l.error_message}</div>}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {prueba && <Etiqueta texto="En seco" clase="bg-slate-100 text-slate-600" />}
        {ingesta && <span className="font-semibold text-[#142D4E] tabular-nums text-[12.5px] sm:w-[84px] sm:text-right">{costo !== null ? usd(costo) : '—'}</span>}
        <span className="sm:w-[84px] flex sm:justify-end"><Etiqueta {...e} /></span>
      </div>
    </div>
  );
}

function Filas({ n = 4 }: { n?: number }) {
  return (
    <div className="divide-y divide-slate-100 border-t border-b border-slate-100">
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="py-3.5 space-y-2"><Relleno ancho={i % 2 ? '45%' : '60%'} /><Relleno ancho="30%" /></div>
      ))}
    </div>
  );
}

function Cifra({ valor, rotulo }: { valor: ReactNode; rotulo: string }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3.5">
      <div className="text-[22px] sm:text-[28px] font-bold text-[#142D4E] leading-none tabular-nums">{valor}</div>
      <div className="text-[11.5px] text-slate-500 mt-1.5">{rotulo}</div>
    </div>
  );
}

function Proceso({ nombre, s }: { nombre: string; s: SchedulerStatus | undefined }) {
  const activo = s?.enabled ?? s?.isRunning ?? false;
  return (
    <div className="py-3 grid grid-cols-1 sm:grid-cols-12 gap-x-4 gap-y-1 items-baseline text-[12.5px]">
      <div className="sm:col-span-4 font-bold text-[#142D4E] flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${activo ? 'bg-emerald-500' : 'bg-slate-300'}`} aria-hidden="true" />
        {nombre}
      </div>
      <div className="sm:col-span-8 text-slate-600 tabular-nums">
        {activo ? cronLegible(s?.schedule) : 'Apagado'}
        {s?.currentlyRunning && <> · <span className="text-indigo-700 font-medium">corriendo ahora</span></>}
        {s?.lastRun && <> · última {cuando(s.lastRun)}</>}
      </div>
    </div>
  );
}

/** Ficha de un usuario en el panel derecho: estado, rol, alta, último acceso, actividad y acciones. */
function FichaUsuario({ u, propia, onClose, onEstado, onRol, onBorrar }: {
  u: Profile; propia: boolean; onClose: () => void; onEstado: (s: 'approved' | 'rejected') => void; onRol: () => void; onBorrar: () => void;
}) {
  const [actividad, setActividad] = useState<ActivityLog[] | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let vivo = true;
    setActividad(null);
    setError(false);
    activityService.getUserActivity(u.id, 10).then(r => {
      if (!vivo) return;
      if (r) setActividad(r); else setError(true);
    });
    return () => { vivo = false; };
  }, [u.id]);
  const estado = estadoDe(u);
  const ultimoAcceso = actividad?.find(a => a.activity_type === 'login')?.created_at;
  return (
    <PaneShell title="Ficha de usuario" onClose={onClose} className="flex-1">
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-200/70">
          <span className="w-10 h-10 rounded-full font-bold text-[13px] flex items-center justify-center shrink-0 bg-[#EEF2FF] text-[#4F46E5]" aria-hidden="true">{iniciales(u)}</span>
          <div className="min-w-0">
            <div className="text-[14.5px] font-bold text-[#142D4E] leading-snug truncate">{nombreDe(u)}{propia && <span className="ml-1.5 text-[11px] font-medium text-slate-500">(tú)</span>}</div>
            <div className="text-[12px] text-slate-500 truncate">{u.email}</div>
          </div>
        </div>
        <div className="divide-y divide-slate-200/70 border-b border-slate-200/70 text-[12.5px]">
          <div className="py-2.5 flex items-center justify-between">
            <span className="text-slate-500">Estado de cuenta</span>
            <Etiqueta {...ETIQUETA_ESTADO[estado]} icono={estado === 'approved' ? <CheckCircle2 className="w-3 h-3" aria-hidden="true" /> : undefined} />
          </div>
          <div className="py-2.5 flex items-center justify-between gap-2">
            <span className="text-slate-500">Rol asignado</span>
            {propia ? (
              <span className="font-semibold text-[#142D4E]" title="No puedes cambiar tu propio rol">{u.role === 'admin' ? 'Admin' : 'Usuario'}</span>
            ) : (
              <button type="button" onClick={onRol} className={`font-semibold text-[#142D4E] hover:underline rounded ${FOCO}`}>
                {u.role === 'admin' ? 'Admin' : 'Usuario'} (cambiar)
              </button>
            )}
          </div>
          <div className="py-2.5 flex items-center justify-between">
            <span className="text-slate-500 flex items-center gap-1.5"><UserPlus className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" /> Alta</span>
            <span className="text-slate-700 tabular-nums">{cuando(u.created_at)}</span>
          </div>
          <div className="py-2.5 flex items-center justify-between">
            <span className="text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" aria-hidden="true" /> Último acceso</span>
            <span className="text-slate-700 tabular-nums">{actividad === null && !error ? '…' : cuando(ultimoAcceso)}</span>
          </div>
        </div>
        <div>
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-2">Actividad reciente</div>
          {error && <p className="text-[12px] text-slate-500">No se pudo cargar su actividad.</p>}
          {!error && actividad === null && <div className="space-y-2"><Relleno ancho="70%" /><Relleno ancho="55%" /></div>}
          {actividad && actividad.length === 0 && <p className="text-[12px] text-slate-500">Sin actividad registrada.</p>}
          {actividad && actividad.length > 0 && (
            <ul className="divide-y divide-slate-200/70 text-[12px] text-slate-600">
              {actividad.map(a => (
                <li key={a.id} className="py-2 flex items-center justify-between gap-3">
                  <span>{ACTIVIDAD[a.activity_type] || a.activity_type}</span>
                  <span className="text-slate-500 tabular-nums">{cuando(a.created_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {propia ? (
          <p className="pt-3 border-t border-slate-200/70 text-[12px] text-slate-500">Es tu cuenta: su estado, su rol y su borrado los cambia otro admin.</p>
        ) : (
          <div className="pt-3 border-t border-slate-200/70 flex flex-wrap items-center justify-between gap-2 text-[12px]">
            {estado !== 'approved'
              ? <button type="button" onClick={() => onEstado('approved')} className={BOTON_ACCION}>Aprobar acceso</button>
              : <button type="button" onClick={onRol} className={`px-3 py-1.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50 font-medium ${FOCO}`}>{u.role === 'admin' ? 'Quitar rol de admin' : 'Hacer admin'}</button>}
            <div className="flex items-center gap-3">
              {estado !== 'rejected' && <button type="button" onClick={() => onEstado('rejected')} className={BOTON_SECUNDARIO}>Rechazar</button>}
              <button type="button" onClick={onBorrar} className={`font-medium text-rose-600 hover:underline rounded ${FOCO}`}>Borrar cuenta</button>
            </div>
          </div>
        )}
      </div>
    </PaneShell>
  );
}

export default function Admin() {
  const { profile: yo } = useAuth();
  const toast = useToast();
  const [pestana, setPestana] = useState<Pestana>('usuarios');
  const [busqueda, setBusqueda] = useState('');
  const [seleccion, setSeleccion] = useState<string | null>(null);
  const [panel, setPanel] = useState(false);
  const [confirmacion, setConfirmacion] = useState<Confirmacion | null>(null);
  const [ejecutando, setEjecutando] = useState(false);
  const [usuarios, setUsuarios] = useState<Profile[] | null>(null);

  const cargarUsuarios = useCallback(() => adminService.getUsers(), []);
  const recursoUsuarios = useRecurso(cargarUsuarios, true);
  useEffect(() => { setUsuarios(recursoUsuarios.datos); }, [recursoUsuarios.datos]);

  const [demos, setDemos] = useState<DemoRequest[] | null>(null);
  const [demoGuardando, setDemoGuardando] = useState<string | null>(null);
  const cargarDemos = useCallback(() => adminService.getDemoRequests(), []);
  // Se carga al entrar a Admin: el contador de «nuevas» es el único aviso de una solicitud (no hay correo).
  const recursoDemos = useRecurso(cargarDemos, true);
  const [demosVisibles, setDemosVisibles] = useState(DEMOS_POR_PAGINA);
  useEffect(() => { setDemos(recursoDemos.datos); }, [recursoDemos.datos]);
  const nDemosNuevas = (demos || []).filter(d => d.estado === 'nueva').length;

  const cargarActividad = useCallback(async () => {
    const [recientes, stats] = await Promise.all([activityService.getRecentActivity(100), activityService.getActivityStats()]);
    if (!recientes || !stats) throw new Error('actividad');
    return { recientes: recientes.data || [], total: recientes.total || 0, stats };
  }, []);
  const actividad = useRecurso<{ recientes: RecentActivityLog[]; total: number; stats: ActivityStats }>(cargarActividad, pestana === 'actividad');
  const cargarIngesta = useCallback(() => adminService.getLogs('processing' as LogKind, 50), []);
  const ingesta = useRecurso(cargarIngesta, pestana === 'ingesta');
  const cargarScraper = useCallback(async () => {
    const [logs, cron] = await Promise.all([adminService.getLogs('scraper' as LogKind, 50), adminService.getCronStatus()]);
    return { logs, cron };
  }, []);
  const scraper = useRecurso<{ logs: ScraperLog[]; cron: CronStatus }>(cargarScraper, pestana === 'scraper');

  useEffect(() => {
    if (!panel) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !confirmacion) setPanel(false); };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [panel, confirmacion]);

  const lista = useMemo(() => usuarios || [], [usuarios]);
  const q = busqueda.trim().toLowerCase();
  const filtrados = lista.filter(u => !q || nombreDe(u).toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  const pendientes = filtrados.filter(u => estadoDe(u) === 'pending');
  const otros = filtrados.filter(u => estadoDe(u) !== 'pending');
  const nPendientes = lista.filter(u => estadoDe(u) === 'pending').length;
  const elegido = lista.find(u => u.id === seleccion) || null;
  const panelAbierto = panel && !!elegido;
  const esPropia = (u: Profile) => u.id === yo?.id;
  const abrirFicha = (u: Profile) => { setSeleccion(u.id); setPanel(true); };

  const ejecutar = async () => {
    if (!confirmacion) return;
    setEjecutando(true);
    try {
      await confirmacion.accion();
      setConfirmacion(null);
    } catch {
      toast.error('No se pudo guardar el cambio');
    } finally {
      setEjecutando(false);
    }
  };

  const pedirEstado = (u: Profile, status: 'approved' | 'rejected') => setConfirmacion(status === 'approved'
    ? { titulo: `¿Aprobar a ${nombreDe(u)}?`, texto: `Podrá entrar a AlertAI con ${u.email}.`, boton: 'Aprobar', accion: async () => {
      await adminService.updateUserStatus(u.id, 'approved');
      setUsuarios(prev => (prev || []).map(x => (x.id === u.id ? { ...x, status: 'approved' } : x)));
      toast.success('Usuario aprobado');
    } }
    : { titulo: `¿Rechazar a ${nombreDe(u)}?`, texto: 'No podrá entrar a AlertAI hasta que un admin lo apruebe. Su cuenta y sus datos se conservan.', boton: 'Rechazar', peligro: true, accion: async () => {
      await adminService.updateUserStatus(u.id, 'rejected');
      setUsuarios(prev => (prev || []).map(x => (x.id === u.id ? { ...x, status: 'rejected' } : x)));
      toast.success('Usuario rechazado');
    } });

  const pedirRol = (u: Profile) => {
    const nuevo = u.role === 'admin' ? 'user' : 'admin';
    setConfirmacion({
      titulo: nuevo === 'admin' ? `¿Hacer admin a ${nombreDe(u)}?` : `¿Quitar el rol de admin a ${nombreDe(u)}?`,
      texto: nuevo === 'admin' ? 'Podrá aprobar, rechazar y borrar cuentas, y ver la actividad, la ingesta y el scraper.' : 'Dejará de ver este panel y no podrá administrar cuentas.',
      boton: nuevo === 'admin' ? 'Hacer admin' : 'Quitar rol',
      accion: async () => {
        await adminService.updateUserRole(u.id, nuevo);
        setUsuarios(prev => (prev || []).map(x => (x.id === u.id ? { ...x, role: nuevo } : x)));
        toast.success(nuevo === 'admin' ? 'Ahora es admin' : 'Rol de admin quitado');
      },
    });
  };

  const pedirBorrar = (u: Profile) => setConfirmacion({
    titulo: `¿Borrar la cuenta de ${nombreDe(u)}?`,
    texto: `Se borran la cuenta ${u.email} y sus favoritos, alertas y conversaciones. No se puede deshacer.`,
    boton: 'Borrar cuenta',
    peligro: true,
    accion: async () => {
      await adminService.deleteUser(u.id);
      setUsuarios(prev => (prev || []).filter(x => x.id !== u.id));
      if (seleccion === u.id) setPanel(false);
      toast.success('Cuenta borrada');
    },
  });

  const cancelar = useCallback(() => setConfirmacion(null), []);

  // Cambiar el estado de una solicitud de demo es reversible: no pide confirmación.
  const cambiarDemo = async (d: DemoRequest, estado: DemoEstado) => {
    if (demoGuardando) return;
    setDemoGuardando(d.id);
    try {
      const nueva = await adminService.updateDemoRequest(d.id, { estado });
      setDemos(prev => (prev || []).map(x => (x.id === d.id ? { ...x, ...nueva, estado } : x)));
      toast.success(`Solicitud de ${d.nombre}: ${ETIQUETA_DEMO[estado].texto.toLowerCase()}`);
    } catch {
      toast.error('No se pudo guardar el cambio');
    } finally {
      setDemoGuardando(null);
    }
  };

  const pildora = (activa: boolean) => `px-3 py-1.5 rounded-full text-[12.5px] transition-colors motion-reduce:transition-none flex items-center gap-1.5 ${FOCO} ${activa ? 'bg-[#EEF2FF] text-[#4F46E5] font-semibold' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`;
  const segmento = (activa: boolean) => `px-2.5 py-1 rounded-full transition-colors motion-reduce:transition-none ${FOCO} ${activa ? 'bg-white text-[#142D4E] font-semibold shadow-2xs' : 'text-slate-500 hover:text-slate-800'}`;

  const subtitulo = recursoUsuarios.error
    ? 'No se pudieron cargar las cuentas.'
    : usuarios === null
      ? 'Cargando cuentas…'
      : `${nf(lista.length)} cuenta${lista.length === 1 ? '' : 's'} registrada${lista.length === 1 ? '' : 's'} · ${nf(nPendientes)} solicitud${nPendientes === 1 ? '' : 'es'} pendiente${nPendientes === 1 ? '' : 's'}`;

  const accionFicha = (
    <button
      type="button"
      onClick={() => setPanel(p => !p)}
      disabled={!elegido}
      aria-label="Ficha de usuario"
      aria-pressed={panelAbierto}
      title={elegido ? 'Ficha de usuario' : 'Elige un usuario para ver su ficha'}
      className={`w-9 h-9 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-40 ${FOCO} ${panelAbierto ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`}
    >
      <PanelRight className="w-5 h-5" />
    </button>
  );

  const filaPendiente = (u: Profile) => (
    <div key={u.id} className="py-3.5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <button type="button" onClick={() => abrirFicha(u)} className={`flex items-center gap-3 min-w-0 text-left rounded-lg ${FOCO}`}>
        <span className="w-8 h-8 rounded-full bg-slate-200/70 text-[#142D4E] font-bold text-[11.5px] flex items-center justify-center shrink-0" aria-hidden="true">{iniciales(u)}</span>
        <span className="min-w-0">
          <span className="block text-[13.5px] font-bold text-[#142D4E] leading-snug truncate">{nombreDe(u)}</span>
          <span className="block text-[12px] text-slate-500 truncate">{u.email}</span>
        </span>
      </button>
      <div className="flex items-center gap-4 text-[12.5px] shrink-0">
        <Etiqueta {...ETIQUETA_ESTADO.pending} />
        <span className="text-slate-500 text-[12px] tabular-nums">solicitó {diaRelativo(u.created_at)}</span>
        <div className="flex items-center gap-2 ml-1">
          <button type="button" onClick={() => pedirEstado(u, 'approved')} className={BOTON_ACCION} aria-label={`Aprobar a ${nombreDe(u)}`}>Aprobar</button>
          <button type="button" onClick={() => pedirEstado(u, 'rejected')} className={BOTON_SECUNDARIO} aria-label={`Rechazar a ${nombreDe(u)}`}>Rechazar</button>
        </div>
      </div>
    </div>
  );

  const filaUsuario = (u: Profile) => {
    const activo = panelAbierto && seleccion === u.id;
    const estado = estadoDe(u);
    const propia = esPropia(u);
    return (
      <div key={u.id} className={`py-3.5 px-3.5 border-l-2 flex items-center justify-between gap-4 group relative transition-colors motion-reduce:transition-none ${activo ? 'bg-slate-50/90 border-l-[#4F46E5]' : 'border-l-transparent hover:bg-slate-50/50'}`}>
        <button type="button" onClick={() => abrirFicha(u)} className={`flex items-center gap-3 min-w-0 flex-1 text-left rounded-lg ${FOCO}`}>
          <span className="w-8 h-8 rounded-full bg-[#EEF2FF] text-[#142D4E] font-bold text-[11.5px] flex items-center justify-center shrink-0" aria-hidden="true">{iniciales(u)}</span>
          <span className="min-w-0">
            <span className="block text-[13.5px] font-bold text-[#142D4E] leading-snug truncate">{nombreDe(u)}{propia && <span className="ml-1.5 text-[11px] font-medium text-slate-500">(tú)</span>}</span>
            <span className="block text-[12px] text-slate-500 truncate">{u.email}</span>
          </span>
        </button>
        <div className="flex items-center gap-3 sm:gap-4 text-[12.5px] shrink-0">
          <span className="hidden md:flex opacity-0 group-hover:opacity-100 text-[11px] text-slate-500 items-center gap-1 transition-opacity motion-reduce:transition-none" aria-hidden="true">
            <PanelRight className="w-3 h-3" /> Ficha
          </span>
          <span className="hidden sm:inline text-slate-500">{u.role === 'admin' ? 'Admin' : 'Usuario'}</span>
          <Etiqueta {...ETIQUETA_ESTADO[estado]} />
          <span className="hidden sm:inline text-slate-500 text-[12px] w-[88px] text-right tabular-nums">alta {diaRelativo(u.created_at)}</span>
          {propia ? <span className="w-7" aria-hidden="true" /> : (
            <MenuUsuario etiqueta={`Acciones para ${nombreDe(u)}`}>
              {cerrar => (
                <>
                  <button type="button" className={itemMenu()} onClick={() => { cerrar(); pedirRol(u); }}>
                    {u.role === 'admin' ? 'Quitar rol de admin' : 'Hacer admin'}
                  </button>
                  {estado !== 'approved' && <button type="button" className={itemMenu()} onClick={() => { cerrar(); pedirEstado(u, 'approved'); }}>Aprobar acceso</button>}
                  {estado !== 'rejected' && <button type="button" className={itemMenu()} onClick={() => { cerrar(); pedirEstado(u, 'rejected'); }}>Rechazar acceso</button>}
                  <button type="button" className={itemMenu(true)} onClick={() => { cerrar(); pedirBorrar(u); }}>Borrar cuenta</button>
                </>
              )}
            </MenuUsuario>
          )}
        </div>
      </div>
    );
  };

  const filaDemo = (d: DemoRequest) => (
    <div key={d.id} className="py-3.5 px-3.5 flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-4 gap-y-2 group">
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-bold text-[#142D4E] leading-snug break-words sm:truncate" title={d.organizacion ? `${d.nombre} · ${d.organizacion}` : d.nombre}>
          {d.nombre}{d.organizacion && <span className="font-normal text-slate-500"> · {d.organizacion}</span>}
        </div>
        <a href={`mailto:${encodeURI(d.correo).replace(/[?&#]/g, c => encodeURIComponent(c))}`} className={`block text-[12px] text-slate-500 hover:text-slate-800 hover:underline truncate rounded ${FOCO}`}>{d.correo}</a>
        <div className="sm:hidden text-[12px] text-slate-500 tabular-nums">{cuando(d.created_at)}</div>
      </div>
      <div className="flex items-center gap-3 sm:gap-4 text-[12.5px] shrink-0">
        <span className="hidden sm:inline text-slate-500 text-[12px] tabular-nums w-[92px] text-right">{cuando(d.created_at)}</span>
        <span className="w-[88px] flex justify-end"><Etiqueta {...ETIQUETA_DEMO[d.estado]} /></span>
        <span className="sm:w-[132px] flex justify-end">
          {d.estado === 'nueva' && (
            <button type="button" disabled={demoGuardando === d.id} onClick={() => cambiarDemo(d, 'contactada')} className={BOTON_ACCION} aria-label={`Marcar como contactada la solicitud de ${d.nombre}`}>
              Marcar contactada
            </button>
          )}
        </span>
        <MenuUsuario etiqueta={`Acciones para la solicitud de ${d.nombre}`}>
          {cerrar => (
            <>
              {ESTADOS_DEMO.filter(e => e !== d.estado).map(e => (
                <button key={e} type="button" className={itemMenu()} onClick={() => { cerrar(); cambiarDemo(d, e); }}>
                  Marcar como {ETIQUETA_DEMO[e].texto.toLowerCase()}
                </button>
              ))}
            </>
          )}
        </MenuUsuario>
      </div>
    </div>
  );

  const costoIngesta = (ingesta.datos || []).reduce((s, l) => s + (costoDe(l) || 0), 0);

  return (
    <Layout title="Admin" actions={accionFicha} rightInset={panelAbierto ? PANES_WIDTH + PANES_GAP * 2 : 0}>
      <Columna ancho={1000}>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3.5 min-w-0">
            <span className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-[#EEF2FF] text-[#4F46E5]" aria-hidden="true">
              <Shield className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-[16px] font-bold text-[#142D4E] leading-snug">Administración</h1>
              <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{subtitulo}</p>
            </div>
          </div>
        </div>

        {/* Pestañas: las colas de cuentas a la izquierda; las operaciones técnicas agrupadas a la derecha */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Cuentas">
            <button type="button" aria-pressed={pestana === 'usuarios'} onClick={() => setPestana('usuarios')} className={pildora(pestana === 'usuarios')}>
              <span>Usuarios</span>{usuarios && <span className="text-[11px] opacity-80 tabular-nums">{nf(lista.length)}</span>}
            </button>
            <button type="button" aria-pressed={pestana === 'pendientes'} onClick={() => setPestana('pendientes')} className={pildora(pestana === 'pendientes')}>
              <span>Pendientes</span>{usuarios && <span className={`text-[11px] font-semibold tabular-nums ${nPendientes ? 'text-amber-700' : 'opacity-80'}`}>{nf(nPendientes)}</span>}
            </button>
            <button type="button" aria-pressed={pestana === 'demos'} onClick={() => setPestana('demos')} className={pildora(pestana === 'demos')}>
              <span>Solicitudes de demo</span>{demos && <span className={`text-[11px] font-semibold tabular-nums ${nDemosNuevas ? 'text-amber-700' : 'opacity-80'}`}>{nf(nDemosNuevas)}</span>}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1 bg-slate-50/90 border border-slate-200/80 rounded-full p-0.5 text-[11.5px]" role="group" aria-label="Operación">
            {([['actividad', 'Actividad'], ['ingesta', 'Ingesta'], ['scraper', 'Scraper']] as [Pestana, string][]).map(([k, t]) => (
              <button key={k} type="button" aria-pressed={pestana === k} onClick={() => setPestana(k)} className={segmento(pestana === k)}>{t}</button>
            ))}
          </div>
        </div>

        {(pestana === 'usuarios' || pestana === 'pendientes') && (
          <>
            {recursoUsuarios.error && (
              <p className="text-[13px] text-slate-500">
                No se pudieron cargar las cuentas.{' '}
                <button type="button" onClick={recursoUsuarios.recargar} className={REINTENTAR}>Reintentar</button>
              </p>
            )}
            {!recursoUsuarios.error && (
              <>
                <div className="mb-4">
                  <div className="flex items-center rounded-xl border border-slate-200/90 bg-white px-3.5 py-2.5 shadow-2xs focus-within:border-slate-300 transition-colors motion-reduce:transition-none">
                    <Search className="w-4 h-4 text-slate-400 mr-2.5 shrink-0" aria-hidden="true" />
                    <input
                      type="search"
                      value={busqueda}
                      onChange={e => setBusqueda(e.target.value)}
                      aria-label="Buscar por nombre o correo"
                      placeholder="Buscar por nombre o correo"
                      className="flex-1 min-w-0 text-[13px] text-slate-900 placeholder:text-slate-500 focus:outline-none bg-transparent"
                    />
                  </div>
                </div>

                {usuarios === null && <Filas />}

                {usuarios !== null && pendientes.length > 0 && (
                  <section aria-label="Solicitudes pendientes" className="rounded-2xl bg-[#FFFDF7] border border-amber-100/80 px-4 divide-y divide-amber-100/60 mb-2">
                    {pendientes.map(filaPendiente)}
                  </section>
                )}

                {usuarios !== null && pestana === 'pendientes' && pendientes.length === 0 && (
                  <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">{q ? 'Ninguna solicitud pendiente coincide con la búsqueda.' : 'No hay solicitudes pendientes.'}</p>
                )}

                {usuarios !== null && pestana === 'usuarios' && (
                  otros.length > 0 ? (
                    <div className="divide-y divide-slate-100 border-b border-slate-100">{otros.map(filaUsuario)}</div>
                  ) : pendientes.length === 0 && (
                    <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">{q ? 'Ninguna cuenta coincide con la búsqueda.' : 'Aún no hay cuentas.'}</p>
                  )
                )}

                {usuarios !== null && lista.length > 0 && (
                  <p className="mt-5 text-[11.5px] text-slate-500 leading-relaxed">
                    Los pendientes van arriba con sus acciones visibles; haz clic en un usuario para ver su actividad y su rol en el panel derecho. Toda acción sobre una cuenta pide confirmación.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {pestana === 'demos' && (
          <section aria-label="Solicitudes de demo">
            {recursoDemos.error && (
              <p className="text-[13px] text-slate-500">No se pudieron cargar las solicitudes de demo. <button type="button" onClick={recursoDemos.recargar} className={REINTENTAR}>Reintentar</button></p>
            )}
            {!recursoDemos.error && demos === null && <Filas />}
            {demos !== null && (demos.length === 0 ? (
              <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">Aún no hay solicitudes de demo. Llegan desde el botón «Solicitar demo» de la portada.</p>
            ) : (
              <>
                <div className="divide-y divide-slate-100 border-t border-b border-slate-100">{demos.slice(0, demosVisibles).map(filaDemo)}</div>
                {demos.length > demosVisibles && (
                  <button type="button" onClick={() => setDemosVisibles(n => n + DEMOS_POR_PAGINA)} className={`mt-3 text-[12.5px] ${REINTENTAR}`}>
                    Cargar más ({nf(demos.length - demosVisibles)} restantes)
                  </button>
                )}
                <p className="mt-3 text-[11.5px] text-slate-500 leading-relaxed">
                  Las más recientes primero. Todavía no se envía ningún correo al recibirlas: esta lista es el único aviso. Al contactar a alguien, márcala como contactada.
                </p>
              </>
            ))}
          </section>
        )}

        {pestana === 'actividad' && (
          <>
            {actividad.error && (
              <p className="text-[13px] text-slate-500">No se pudo cargar la actividad. <button type="button" onClick={actividad.recargar} className={REINTENTAR}>Reintentar</button></p>
            )}
            {actividad.cargando && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  {Array.from({ length: 4 }, (_, i) => <div key={i} className="rounded-2xl border border-slate-200/80 bg-white px-4 py-3.5 space-y-2"><Relleno ancho="40%" /><Relleno ancho="70%" /></div>)}
                </div>
                <Filas />
              </>
            )}
            {actividad.datos && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                  <Cifra valor={nf(actividad.datos.stats.today_logins || 0)} rotulo="Inicios de sesión hoy" />
                  <Cifra valor={nf(actividad.datos.stats.this_week_logins || 0)} rotulo="Esta semana" />
                  <Cifra valor={nf(actividad.datos.stats.this_month_logins || 0)} rotulo="Este mes" />
                  <Cifra valor={nf(actividad.datos.stats.unique_users || 0)} rotulo="Usuarios que han entrado" />
                </div>
                {actividad.datos.recientes.length === 0 ? (
                  <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">Aún no hay actividad registrada.</p>
                ) : (
                  <div className="divide-y divide-slate-100 border-t border-b border-slate-100 text-[13px]">
                    {actividad.datos.recientes.map(a => {
                      const Icono = ICONO_ACTIVIDAD[a.activity_type] || LogIn;
                      return (
                        <div key={a.id} className="py-3 flex items-center justify-between gap-4">
                          <div className="min-w-0 flex items-start gap-2.5">
                            <Icono className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" aria-hidden="true" />
                            <div className="min-w-0">
                              <div className="truncate">
                                <span className="font-bold text-[#142D4E]">{a.user_name || a.user_email}</span>{' '}
                                <span className="text-slate-600">· {ACTIVIDAD[a.activity_type] || a.activity_type}</span>
                              </div>
                              <div className="text-[12px] text-slate-500 truncate tabular-nums">{[a.user_email, a.ip_address && `IP ${a.ip_address}`].filter(Boolean).join(' · ')}</div>
                            </div>
                          </div>
                          <span className="text-[12px] text-slate-500 shrink-0 tabular-nums">{cuando(a.created_at)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-3 text-[11.5px] text-slate-500">
                  {nf(actividad.datos.stats.total_logins || 0)} inicios de sesión en total.
                  {actividad.datos.total > actividad.datos.recientes.length && <> Se muestran los {nf(actividad.datos.recientes.length)} eventos más recientes de {nf(actividad.datos.total)}.</>}
                </p>
              </>
            )}
          </>
        )}

        {pestana === 'ingesta' && (
          <>
            {ingesta.error && (
              <p className="text-[13px] text-slate-500">No se pudieron cargar las corridas de ingesta. <button type="button" onClick={ingesta.recargar} className={REINTENTAR}>Reintentar</button></p>
            )}
            {ingesta.cargando && <Filas />}
            {ingesta.datos && (ingesta.datos.length === 0 ? (
              <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">Aún no hay corridas de ingesta registradas.</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-5">
                  <Cifra valor={usd(costoIngesta)} rotulo={`Costo de las ${nf(ingesta.datos.length)} corridas mostradas`} />
                  <Cifra valor={nf(ingesta.datos.reduce((s, l) => s + (l.documents_processed || 0), 0))} rotulo="Documentos procesados en ellas" />
                  <Cifra valor={nf(ingesta.datos.filter(l => l.status === 'failed').length)} rotulo="Corridas que fallaron" />
                </div>
                <div className="divide-y divide-slate-100 border-t border-b border-slate-100 text-[13px]">
                  {ingesta.datos.map(l => <FilaCorrida key={l.id} l={l} ingesta />)}
                </div>
                <p className="mt-3 text-[11.5px] text-slate-500">Desde la más reciente; el costo es el que registró cada corrida (llamadas a modelos y embeddings).</p>
              </>
            ))}
          </>
        )}

        {pestana === 'scraper' && (
          <>
            {scraper.error && (
              <p className="text-[13px] text-slate-500">No se pudo cargar el estado del scraper. <button type="button" onClick={scraper.recargar} className={REINTENTAR}>Reintentar</button></p>
            )}
            {scraper.cargando && <Filas />}
            {scraper.datos && (
              <>
                <section aria-label="Procesos programados" className="rounded-2xl border border-slate-200/80 bg-white px-4 divide-y divide-slate-100 mb-5">
                  <Proceso nombre="Scraper del SII" s={scraper.datos.cron.schedulers.scraper} />
                  <Proceso nombre="Resumen diario por correo" s={scraper.datos.cron.schedulers.notification} />
                </section>
                {scraper.datos.logs.length === 0 ? (
                  <p className="text-[13px] text-slate-500 border-t border-slate-100 pt-3.5">Aún no hay corridas del scraper registradas.</p>
                ) : (
                  <div className="divide-y divide-slate-100 border-t border-b border-slate-100 text-[13px]">
                    {scraper.datos.logs.map(l => <FilaCorrida key={l.id} l={l} ingesta={false} />)}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Columna>

      <AnimatePresence>
      {panelAbierto && elegido && (
        <RightPanes key="panel-derecho">
          <FichaUsuario
            u={elegido}
            propia={esPropia(elegido)}
            onClose={() => setPanel(false)}
            onEstado={s => pedirEstado(elegido, s)}
            onRol={() => pedirRol(elegido)}
            onBorrar={() => pedirBorrar(elegido)}
          />
        </RightPanes>
      )}
      </AnimatePresence>

      {confirmacion && <Confirmar c={confirmacion} ejecutando={ejecutando} onCancelar={cancelar} onConfirmar={ejecutar} />}
    </Layout>
  );
}
